import React from 'react';
import styled, { css } from 'styled-components';
import { OnlineThemeType } from '../../themes/online';

export enum ButtonColor {
  'default' = 'colorDefault',
  'light' = 'colorLight',
  'dark' = 'colorDark',
  'alternate' = 'colorAlternate',
  'neutral' = 'colorNeutral',
  'primaryLightBlue' = 'colorLightBlue',
}

export enum ButtonType {
  'Button' = 'button',
  'Submit' = 'submit',
}

export enum ButtonSize {
  'small' = 'sizeSmall',
  'medium' = 'sizeMedium',
  'mediumLarge' = 'mediumLarge',
  'large' = 'sizeLarge',
}

function buttonSize(size: ButtonSize, theme: OnlineThemeType) {
  switch (size) {
    case ButtonSize.small:
      return `
        height: 18px;
        font-size: 10px;
        padding: 0 ${theme.spacing.medium}px;
      `;
    case ButtonSize.medium:
      return `
        height: 24px;
        font-size: 14px;
        padding: 0 ${theme.spacing.large}px;
      `;
    case ButtonSize.mediumLarge:
      return `
        height: 30px;
        font-size: 16px;
        padding: 0 ${theme.spacing.large}px;
      `;
    default:
    case ButtonSize.large:
      return `
        min-height: 44px;
        font-size: 20px;
        padding: 0 ${theme.spacing.large}px;
      `;
  }
}

function buttonColor(color: ButtonColor, theme: OnlineThemeType) {
  switch (color) {
    case ButtonColor.dark:
      return `
        background-color: ${theme.colors.dark};
      `;
    case ButtonColor.light:
      return `
        background-color: ${theme.colors.light};
      `;
    case ButtonColor.alternate:
      return `
        background-color: ${theme.colors.sunbitYellow};
      `;
    case ButtonColor.primaryLightBlue:
      return `
      background-color: ${theme.colors.primaryLightBlue};
      `;
    case ButtonColor.default:
    default:
      return `
        background-color: ${theme.colors.grayDark};
      `;
  }
}

const disabledStyle = ({ theme }: { theme: OnlineThemeType }) => {
  return css`
    background-color: ${theme.colors.disabledButton};
    cursor: default;
  `;
};

// tslint:disable-next-line:variable-name
export const SButton = styled.button<{ stretch: boolean; disabled: boolean; size: ButtonSize; color: ButtonColor }>(
  ({ theme, stretch, disabled, size, color }) => css`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    color: ${theme.colors.white};
    font-weight: ${theme.font.weight.strong};
    border: 1px solid transparent;
    border-radius: ${theme.borderRadiusCommon}px;
    outline: none;
    cursor: pointer;
    user-select: none;
    width: ${stretch ? '100%' : 'auto'};
    ${buttonSize(size, theme)};
    ${buttonColor(color, theme)};

    ${disabled && disabledStyle};

    &:before {
      content: '';
      position: absolute;
      border-radius: ${theme.borderRadiusCommon}px;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      background-color: black;
    }

    &:focus {
      outline: 2px solid ${theme.colors.dark};
    }

    @media only screen and (min-width: 800px) {
      &:hover {
        &:before {
          opacity: ${disabled ? 0 : 0.1};
        }
      }
    }
  `
);

export interface IButtonProps {
  onClick?: React.MouseEventHandler;
  onKeyUp?: React.KeyboardEventHandler;
  stretch: boolean;
  color: ButtonColor;
  size: ButtonSize;
  disabled: boolean;
  dataAt?: string;
  className?: string;
  type?: ButtonType;
  id?: string;
  children?: React.ReactNode;
}

export function Button({ dataAt, ...restProps }: IButtonProps): JSX.Element {
  return <SButton {...{ ...restProps, 'data-at': dataAt }} />;
}

Button.defaultProps = {
  stretch: false,
  color: ButtonColor.default,
  size: ButtonSize.large,
  disabled: false,
  id: '',
  type: ButtonType.Button,
};
